import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { PageDto } from '../../../data/models/dto/arrival/page.dto';
import { UserActivationDto } from '../../../data/models/dto/user-activation.dto';
import { UserParamsDto } from '../../../data/models/dto/user-params.dto';
import { Profile } from '../../../data/models/interfaces/profile.interface';
import { UsersService } from '../../../data/services/user.service';
import { SelectedUserUtil } from '../../../data/utils/selected-user.utils';
import { YearSelectComponent } from '../../components/year-select/year-select.component';
import {
  MANAGEMENT_PROFILES,
  RESTRICTED_ROUTE_PROFILES,
} from '../../../data/utils/profile.utils';

@Component({
  selector: 'app-user-list',
  templateUrl: './user-list.component.html',
  styleUrls: ['./user-list.component.scss'],
})
export class UserListComponent implements OnInit {
  @Output() scrollDown = new EventEmitter<string>();
  @ViewChild('yearSelectId') select!: YearSelectComponent;

  profilesEligibleToCreateUser = RESTRICTED_ROUTE_PROFILES(MANAGEMENT_PROFILES);
  prod: Profile[] = [];
  users: UserActivationDto[] = [];
  profile: Profile[] = [];

  isInProgress = false;
  loading: boolean = false;

  //scroll
  throttle = 50;
  scrollDistance = 1;
  scrollUpDistance = 2;
  disableScrollFetch = false;

  //pagination
  params: UserParamsDto = { page: 1, limit: 33 };
  idsSchool?: string[];
  countUsers?: number;

  //filters
  classroomsFilter: string[] = [];
  profilesFilter: string[] = [];
  yearFilter: string = '';
  currentYear: string = new Date().getFullYear().toString();
  notCurrentYear: boolean = false;
  disableResetFilters = true;
  searchText: string = '';
  activeUsersFilter = true;

  private readonly selectedYearKey = 'userListSelectedYear';
  private readonly selectedIdsClassroomsKey = 'userListSelectedIdsClassrooms';
  private readonly selectedProfilesKey = 'userListSelectedProfiles';
  private readonly selectedActiveUsersKey = 'userListSelectedActiveUsers';

  constructor(
    private userService: UsersService,
    private userUtil: UserUtil,
    private router: Router,
    private data: SelectedUserUtil,
  ) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;

    this.idsSchool = this.userUtil.getSchools().map((school) => school.id);
    await this.getStoredData();
  }

  async getStoredData() {
    const yearStored = localStorage.getItem(this.selectedYearKey);
    if (yearStored) {
      this.yearFilter = yearStored;
    }

    const classroomsStored = localStorage.getItem(
      this.selectedIdsClassroomsKey,
    );
    if (classroomsStored) {
      this.classroomsFilter = classroomsStored.split(',');
    }

    const profilesStored = localStorage.getItem(this.selectedProfilesKey);
    if (profilesStored) {
      this.profilesFilter = profilesStored.split(',');
    }

    const activeUsersStored = localStorage.getItem(this.selectedActiveUsersKey);
    if (activeUsersStored !== null) {
      this.activeUsersFilter = activeUsersStored === 'true';
    }
  }

  async getAll(params?: UserParamsDto): Promise<PageDto<UserActivationDto>> {
    this.loading = true;
    const resp = await this.userService.getAllV2(params).toPromise();
    this.countUsers = resp.total;

    this.loading = false;

    return resp;
  }

  async filter(value: string) {
    if (this.searchText != value) {
      this.searchText = value;
      if (value == undefined || value == '') {
        this.disableScrollFetch = false;
      }
      await this.handleFilter();
    }
  }

  private async handleFilter() {
    let text =
      this.searchText && this.searchText.length >= 3
        ? this.searchText.toLowerCase()
        : undefined;

    this.params.page = 1;
    const params = this.buildParams(
      this.params?.limit,
      1,
      this.idsSchool,
      text,
    );
    const users = await this.getAll(params);
    this.users = users.data;
    this.resetFiltersStatus();
  }

  async handleSelectClassroom(ids: string[]) {
    this.classroomsFilter = ids;
    this.saveSelectedClassroom();
    await this.handleFilter();
  }

  async handleSelectProfiles(ids: string[]) {
    this.profilesFilter = ids;
    this.saveSelectedProfiles();
    await this.handleFilter();
  }

  async handleSelectActiveUsers(active: boolean) {
    this.activeUsersFilter = active;
    this.saveSelectedActiveUsers();
    await this.handleFilter();
  }

  isNotCurrentYear(): boolean {
    if (this.yearFilter != this.currentYear) {
      this.notCurrentYear = true;
    } else {
      this.notCurrentYear = false;
    }
    return this.notCurrentYear;
  }

  async handleSelectYear(year: string) {
    if (this.yearFilter && this.yearFilter !== year) {
      this.classroomsFilter = [];
      localStorage.removeItem(this.selectedIdsClassroomsKey);
    }
    this.yearFilter = year;
    this.saveSelectedYear(year);
    this.isNotCurrentYear();
    await this.handleFilter();
  }

  async handleClearFilter() {
    this.select.reset();
    this.classroomsFilter = [];
    this.profilesFilter = [];
    this.activeUsersFilter = true;

    localStorage.removeItem(this.selectedIdsClassroomsKey);
    localStorage.removeItem(this.selectedYearKey);
    localStorage.removeItem(this.selectedProfilesKey);
    localStorage.removeItem(this.selectedActiveUsersKey);

    await this.handleFilter();
  }

  resetFiltersStatus() {
    this.disableResetFilters =
      !this.isNotCurrentYear() &&
      this.classroomsFilter.length == 0 &&
      this.profilesFilter.length == 0 &&
      this.activeUsersFilter;
  }

  async updatePage(id: string): Promise<void> {
    if (!this.isInProgress) {
      this.isInProgress = true;
      const user = await this.userService.getById(id).toPromise();
      this.data.setSelectedUser(user);
      if (await this.router.navigate(['user-form'])) {
        this.isInProgress = false;
      }
    }
  }

  private buildParams(
    limit?: number,
    page?: number,
    idsSchool?: string[],
    name?: string,
  ): UserParamsDto {
    return {
      limit: limit,
      page: page,
      idsSchool: idsSchool,
      name: name,
      idsProfiles: this.profilesFilter,
      idsClassRooms: this.classroomsFilter,
      active: this.activeUsersFilter,
    };
  }

  async onScrollDown(ev: any) {
    if (!this.disableScrollFetch) {
      this.params.page! += 1;

      let text =
        this.searchText && this.searchText.length >= 3
          ? this.searchText.toLowerCase()
          : undefined;

      const params = this.buildParams(
        this.params?.limit,
        this.params.page,
        this.idsSchool,
        text,
      );
      const result = await this.getAll(params);

      this.addItems(result.data);
    }
  }

  onClassroomsLoaded(success: boolean) {
    if (!success) {
      this.loading = false;
      this.disableScrollFetch = true;
    } else {
      this.handleFilter();
    }
  }

  addItems(items: UserActivationDto[]) {
    this.users.push(...items);

    this.users = this.removeDuplicate(this.users);
  }

  private removeDuplicate(list: any[]) {
    const unique = Array.from(new Set(list.map((a) => a.id))).map((id) => {
      return list.find((a) => a.id === id);
    });
    return unique;
  }

  private saveSelectedYear(year: string) {
    localStorage.setItem(this.selectedYearKey, year);
  }

  private saveSelectedClassroom() {
    localStorage.setItem(
      this.selectedIdsClassroomsKey,
      this.classroomsFilter.join(','),
    );
  }

  private saveSelectedProfiles() {
    localStorage.setItem(
      this.selectedProfilesKey,
      this.profilesFilter.join(','),
    );
  }

  private saveSelectedActiveUsers() {
    localStorage.setItem(
      this.selectedActiveUsersKey,
      this.activeUsersFilter.toString(),
    );
  }
}
